import { ChatWidget } from "components/ChatWidget";
import { currentPageUrl } from "components/ChatWidget/chatSupport.helpers";
import { useStaffChatPageHints } from "context/StaffChatPageHintsContext";
import { useContractManagementBot } from "hooks/useContractManagementBot";
import { useLocation } from "react-router-dom";

import {
  isHospitalPage,
  resolveStaffChatSurface,
  withFocusedOrder,
  withPageHints,
} from "./staffChatWidget.helpers";
import { useStaffChatAccountId } from "./useStaffChatAccountId";

/**
 * CLE-3043 — the ONE staff chat widget, mounted once by AppLayout for every logged-in page. It used to
 * be mounted separately by Smart Insights, the Contracts Library tab and the contract page.
 *
 * Shown only on a hospital page (isHospitalPage), for the page's account (useStaffChatAccountId — on an
 * estimate page the estimate's own account; nothing at all while that is loading or if it cannot be read),
 * when that account passes canUseContractManagementBot (bot assigned; SuperAdmin, or the account's
 * Hospital Staff Access switch on). The widget itself then applies the global rollout switch (`staffAccessEnabled`) for non-admins.
 *
 * Keyed by account, so switching accounts starts the widget afresh instead of carrying one account's
 * chat into another's pages. The widget sends that account on its own requests and never changes the
 * app's current account.
 *
 * Staff see it as "Clear Health AI" with a greeting by first name, no suggested questions and no
 * chat-history list (2026-10-29, Ani after the demo); admin pages still call it the Hospital Staff Bot.
 * The title, subtitle and greeting are ChatWidget's own staff defaults (hospitalStaffBot.constants).
 *
 * On the create / review / copy / edit estimate pages the context also carries
 * `page.blockingIssues` — why "Create and Send" is disabled, as labels (StaffChatPageHintsContext) —
 * and `page.form`, what has been typed into the form so far (buildEstimateFormSnapshot), so "this
 * patient" on the New Estimate page means the form. The review / copy pages also send their
 * estimate's id (resolveStaffChatSurface): the API reads the saved estimate and the typed form
 * beside it, so an edit not saved yet (a changed payer, an added code) is what the bot answers from.
 * On a page with no estimate of its own (an Estimates list, a hospital tab) it carries `orderId` of
 * the row the user expanded last (OrdersTable → `focusedOrderId`), so "this patient" means that row.
 */
export function StaffChatWidget(): JSX.Element | null {
  const location = useLocation();
  const { accountId, isResolving } = useStaffChatAccountId();
  const canUse = useContractManagementBot(accountId);
  // The create / review / copy / edit estimate pages publish why Create is disabled and what has
  // been typed, and the Estimates lists the row in focus (CLE-3043).
  const { blockingIssues, focusedOrderId, form } = useStaffChatPageHints();

  if (
    isResolving ||
    !accountId ||
    !canUse ||
    !isHospitalPage(location.pathname)
  ) {
    return null;
  }

  const { contextData: surfaceContextData } = resolveStaffChatSurface(
    location.pathname,
    currentPageUrl()
  );
  const contextData = withPageHints(
    withFocusedOrder(surfaceContextData, focusedOrderId),
    blockingIssues,
    form
  );

  return (
    <ChatWidget
      key={accountId}
      accountId={accountId}
      chatType="ContractManagementBot"
      position="fixed"
      contextData={contextData}
    />
  );
}
